/**
 * Shader-based thruster exhaust effect.
 * Replaces the particle-based ThrusterEffect with a single PlaneGeometry
 * and custom ShaderMaterial that procedurally paints a continuous flame.
 *
 * One draw call per thruster. All visual complexity (turbulence, Mach diamonds,
 * color gradients, HDR bloom) is computed in the fragment shader.
 *
 * Props: { active, afterburner, reverse } — identical to old ThrusterEffect.
 *
 * Subscribes to particleEffectStore to apply flame config as shader uniforms.
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import {
  thrusterVertexShader,
  thrusterFragmentShader,
} from '../shaders/thrusterShaders.js';
import { ThrusterSparks } from './ThrusterSparks.js';
import { useParticleEffectStore } from '../../stores/particleEffectStore.js';
import type { ThrusterFlameConfig } from '@warmancer/shared';
import { DEFAULT_THRUSTER_FLAME } from '@warmancer/shared';

interface ThrusterExhaustProps {
  readonly active: boolean;
  readonly afterburner: boolean;
  readonly reverse?: boolean;
}

/** Lerp speed for intensity activation (units per second). */
const INTENSITY_LERP_SPEED = 12;

/** Lerp speed for afterburner mode transition (units per second). */
const AFTERBURNER_LERP_SPEED = 8;

/** Below this intensity, snap to zero and hide mesh. */
const INTENSITY_SNAP_THRESHOLD = 0.01;

export function ThrusterExhaust({ active, afterburner, reverse = false }: ThrusterExhaustProps) {
  const meshRef = useRef<THREE.Mesh>(null);

  // Subscribe to flame config from store for geometry sizing (React render path).
  // draftOverrides has priority (particle editor), then effects (live game), then defaults.
  const flameConfig = useParticleEffectStore((s) =>
    (s.draftOverrides.thruster_flame as ThrusterFlameConfig | undefined)
    ?? (s.effects.thruster_flame as ThrusterFlameConfig | undefined)
    ?? DEFAULT_THRUSTER_FLAME,
  );

  // Stable uniforms object — same reference across renders
  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uIntensity: { value: 0 },
      uAfterburner: { value: 0 },
      uFlickerSeed: { value: Math.random() },
      // Configurable uniforms — initialized from defaults
      uNozzleWidth: { value: DEFAULT_THRUSTER_FLAME.nozzleWidth },
      uPeakWidth: { value: DEFAULT_THRUSTER_FLAME.peakWidth },
      uFlameLen: { value: DEFAULT_THRUSTER_FLAME.flameLenNormal },
      uFlameLenAB: { value: DEFAULT_THRUSTER_FLAME.flameLenAfterburner },
      uFlowSpeed: { value: DEFAULT_THRUSTER_FLAME.flowSpeed },
      uFlowSpeedBoost: { value: DEFAULT_THRUSTER_FLAME.flowSpeedBoost },
      uDistortionBase: { value: DEFAULT_THRUSTER_FLAME.distortionBase },
      uDistortionTipAdd: { value: DEFAULT_THRUSTER_FLAME.distortionTipAdd },
      uMachFreqNormal: { value: DEFAULT_THRUSTER_FLAME.machFreqNormal },
      uMachFreqAB: { value: DEFAULT_THRUSTER_FLAME.machFreqAfterburner },
      uMachBandMin: { value: DEFAULT_THRUSTER_FLAME.machBandMin },
      uMachBandRange: { value: DEFAULT_THRUSTER_FLAME.machBandRange },
      uTurbCoarseScale: { value: DEFAULT_THRUSTER_FLAME.turbCoarseScale },
      uTurbFineScale: { value: DEFAULT_THRUSTER_FLAME.turbFineScale },
      uTurbBase: { value: DEFAULT_THRUSTER_FLAME.turbBase },
      uTurbCoarseAmp: { value: DEFAULT_THRUSTER_FLAME.turbCoarseAmp },
      uTurbFineAmp: { value: DEFAULT_THRUSTER_FLAME.turbFineAmp },
      uFlickerGlobalFreq: { value: DEFAULT_THRUSTER_FLAME.flickerGlobalFreq },
      uFlickerGlobalAmp: { value: DEFAULT_THRUSTER_FLAME.flickerGlobalAmp },
      uFlickerLocalFreq: { value: DEFAULT_THRUSTER_FLAME.flickerLocalFreq },
      uFlickerLocalAmp: { value: DEFAULT_THRUSTER_FLAME.flickerLocalAmp },
      uNormalCore: { value: new THREE.Vector3(...DEFAULT_THRUSTER_FLAME.normalCore) },
      uNormalMid: { value: new THREE.Vector3(...DEFAULT_THRUSTER_FLAME.normalMid) },
      uNormalTip: { value: new THREE.Vector3(...DEFAULT_THRUSTER_FLAME.normalTip) },
      uAbCore: { value: new THREE.Vector3(...DEFAULT_THRUSTER_FLAME.abCore) },
      uAbMid: { value: new THREE.Vector3(...DEFAULT_THRUSTER_FLAME.abMid) },
      uAbTip: { value: new THREE.Vector3(...DEFAULT_THRUSTER_FLAME.abTip) },
      uHdrMultNormal: { value: DEFAULT_THRUSTER_FLAME.hdrMultNormal },
      uHdrMultAB: { value: DEFAULT_THRUSTER_FLAME.hdrMultAfterburner },
      uNozzleBoostMax: { value: DEFAULT_THRUSTER_FLAME.nozzleBoostMax },
    }),
    [],
  );

  // Animate uniforms per frame
  useFrame((_, delta) => {
    const mesh = meshRef.current;
    if (!mesh) return;

    // Smooth lerp toward target intensity
    const targetIntensity = active ? 1.0 : 0.0;
    const intensityStep = INTENSITY_LERP_SPEED * delta;
    uniforms.uIntensity.value += (targetIntensity - uniforms.uIntensity.value) * Math.min(1, intensityStep);

    // Snap to zero below threshold (no ghost flame)
    if (uniforms.uIntensity.value < INTENSITY_SNAP_THRESHOLD && !active) {
      uniforms.uIntensity.value = 0;
    }

    // Hide mesh when fully inactive (zero GPU cost)
    mesh.visible = uniforms.uIntensity.value > 0;

    if (!mesh.visible) return;

    // Smooth lerp toward target afterburner mode
    const targetAfterburner = afterburner ? 1.0 : 0.0;
    const abStep = AFTERBURNER_LERP_SPEED * delta;
    uniforms.uAfterburner.value += (targetAfterburner - uniforms.uAfterburner.value) * Math.min(1, abStep);

    // Advance time
    uniforms.uTime.value += delta;

    // Read config via getState() — bypasses React rendering entirely.
    // draftOverrides is set by the particle editor; effects is the live game config.
    const store = useParticleEffectStore.getState();
    const cfg = (store.draftOverrides.thruster_flame as ThrusterFlameConfig | undefined)
      ?? (store.effects.thruster_flame as ThrusterFlameConfig | undefined)
      ?? DEFAULT_THRUSTER_FLAME;
    uniforms.uNozzleWidth.value = cfg.nozzleWidth;
    uniforms.uPeakWidth.value = cfg.peakWidth;
    uniforms.uFlameLen.value = cfg.flameLenNormal;
    uniforms.uFlameLenAB.value = cfg.flameLenAfterburner;
    uniforms.uFlowSpeed.value = cfg.flowSpeed;
    uniforms.uFlowSpeedBoost.value = cfg.flowSpeedBoost;
    uniforms.uDistortionBase.value = cfg.distortionBase;
    uniforms.uDistortionTipAdd.value = cfg.distortionTipAdd;
    uniforms.uMachFreqNormal.value = cfg.machFreqNormal;
    uniforms.uMachFreqAB.value = cfg.machFreqAfterburner;
    uniforms.uMachBandMin.value = cfg.machBandMin;
    uniforms.uMachBandRange.value = cfg.machBandRange;
    uniforms.uTurbCoarseScale.value = cfg.turbCoarseScale;
    uniforms.uTurbFineScale.value = cfg.turbFineScale;
    uniforms.uTurbBase.value = cfg.turbBase;
    uniforms.uTurbCoarseAmp.value = cfg.turbCoarseAmp;
    uniforms.uTurbFineAmp.value = cfg.turbFineAmp;
    uniforms.uFlickerGlobalFreq.value = cfg.flickerGlobalFreq;
    uniforms.uFlickerGlobalAmp.value = cfg.flickerGlobalAmp;
    uniforms.uFlickerLocalFreq.value = cfg.flickerLocalFreq;
    uniforms.uFlickerLocalAmp.value = cfg.flickerLocalAmp;
    uniforms.uNormalCore.value.set(...cfg.normalCore);
    uniforms.uNormalMid.value.set(...cfg.normalMid);
    uniforms.uNormalTip.value.set(...cfg.normalTip);
    uniforms.uAbCore.value.set(...cfg.abCore);
    uniforms.uAbMid.value.set(...cfg.abMid);
    uniforms.uAbTip.value.set(...cfg.abTip);
    uniforms.uHdrMultNormal.value = cfg.hdrMultNormal;
    uniforms.uHdrMultAB.value = cfg.hdrMultAfterburner;
    uniforms.uNozzleBoostMax.value = cfg.nozzleBoostMax;
  });

  return (
    <group rotation={reverse ? [0, Math.PI, 0] : undefined}>
      {/* Plane rotated -PI/2 around X to lie flat on XZ plane.
          UV.y=0 at nozzle (origin side), flame extends along +Z.
          Offset +Z by half length so nozzle sits at group origin. */}
      <mesh
        ref={meshRef}
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0, flameConfig.flameLength / 2]}
        visible={false}
      >
        <planeGeometry args={[flameConfig.flameWidth, flameConfig.flameLength]} />
        <shaderMaterial
          vertexShader={thrusterVertexShader}
          fragmentShader={thrusterFragmentShader}
          uniforms={uniforms}
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          side={THREE.DoubleSide}
        />
      </mesh>
      <ThrusterSparks active={active} afterburner={afterburner} />
    </group>
  );
}
